import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiRequestError } from "../api/client";
import { useCurrentCase } from "../hooks/useCurrentCase";
import { CREDIT_RATES, UPLOAD_KINDS, type UploadKind } from "../../shared/constants";
import { WizardSteps } from "../components/WizardSteps";

interface UploadItem {
  id: string;
  kind: UploadKind;
  filename: string | null;
  content_type: string | null;
  size: number | null;
  url: string;
}

const REQUIRED_ASSETS: Array<{ kind: UploadKind; name: string; note: string; required: boolean; accept?: string }> = [
  { kind: "face", name: "代表写真", note: "使いたい写真、またはAI生成用の顔写真を複数枚。", required: true, accept: "JPG / PNG" },
  { kind: "logo", name: "ロゴ", note: "PNG / SVG。なければ後から文字ロゴでも作れます。", required: false, accept: "PNG / SVG" },
  { kind: "office", name: "事務所写真", note: "相談室・受付・外観など、安心感が伝わる写真。", required: false, accept: "JPG / PNG" },
  { kind: "testimonial", name: "お客様の声", note: "画像でも、チャットで入力した文章でも使えます。", required: false, accept: "画像 / テキスト" },
  { kind: "fee_table", name: "料金表", note: "PDF・画像・テキスト。決まっている範囲だけで大丈夫です。", required: false, accept: "PDF / 画像 / テキスト" },
];

function fileSize(n: number | null): string {
  if (!n) return "";
  if (n > 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)}MB`;
  return `${Math.max(1, Math.round(n / 1024))}KB`;
}

function isImage(ct: string | null): boolean {
  return !!ct && ct.startsWith("image/");
}

function statusFor(count: number, required: boolean): { label: string; tone: string } {
  if (count > 0) return { label: "確認済み", tone: "green" };
  if (required) return { label: "不足", tone: "amber" };
  return { label: "確認必要", tone: "" };
}

export function AssetsPage() {
  const { caseRow, loading: caseLoading } = useCurrentCase();
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyKind, setBusyKind] = useState<UploadKind | null>(null);
  const [textSaving, setTextSaving] = useState<string | null>(null);
  const [assetTexts, setAssetTexts] = useState({ testimonial_text: "", fee_table_text: "" });
  const [choiceOpenKind, setChoiceOpenKind] = useState<UploadKind | null>(null);
  const [textOpen, setTextOpen] = useState({ testimonial_text: false, fee_table_text: false });
  const [error, setError] = useState<string | null>(null);

  const reloadUploads = async () => {
    if (!caseRow) return;
    const res = await api.get<{ uploads: UploadItem[] }>(`/api/cases/${caseRow.id}/uploads`);
    setUploads(res.uploads);
  };

  const reloadTextAssets = async () => {
    if (!caseRow) return;
    const res = await api.get<{ hearing: Record<string, unknown> }>(`/api/cases/${caseRow.id}/hearing`);
    setAssetTexts({
      testimonial_text: String(res.hearing.testimonial_text ?? ""),
      fee_table_text: String(res.hearing.fee_table_text ?? res.hearing.pricing_note ?? ""),
    });
  };

  const reload = async () => {
    if (!caseRow) return;
    setLoading(true);
    try {
      await Promise.all([reloadUploads(), reloadTextAssets()]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (caseRow) void reload();
    if (!caseLoading && !caseRow) setLoading(false);
  }, [caseRow?.id, caseLoading]);

  const onFiles = async (kind: UploadKind, files: FileList | null) => {
    if (!caseRow || !files || files.length === 0) return;
    setBusyKind(kind);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        const form = new FormData();
        form.append("kind", kind);
        form.append("file", file);
        await api.upload(`/api/cases/${caseRow.id}/uploads`, form);
      }
      await reloadUploads();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "アップロードに失敗しました");
    } finally {
      setBusyKind(null);
    }
  };

  const saveTextAsset = async (key: "testimonial_text" | "fee_table_text") => {
    if (!caseRow) return;
    setTextSaving(key);
    setError(null);
    try {
      await api.patch(`/api/cases/${caseRow.id}/asset-texts`, { [key]: assetTexts[key] });
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "テキスト素材の保存に失敗しました");
    } finally {
      setTextSaving(null);
    }
  };

  const byKind = useMemo(() => {
    const map = new Map<UploadKind, UploadItem[]>();
    for (const kind of UPLOAD_KINDS) map.set(kind.value, []);
    for (const upload of uploads) map.get(upload.kind)?.push(upload);
    return map;
  }, [uploads]);

  const hasTextAsset = (kind: UploadKind) => {
    if (kind === "testimonial") return Boolean(assetTexts.testimonial_text.trim());
    if (kind === "fee_table") return Boolean(assetTexts.fee_table_text.trim());
    return false;
  };
  const confirmedCount = REQUIRED_ASSETS.filter((asset) => (byKind.get(asset.kind)?.length ?? 0) > 0 || hasTextAsset(asset.kind)).length;

  if (caseLoading || loading) return <div className="page-center">読み込み中...</div>;
  if (!caseRow) return <div className="page-center">案件が見つかりません。</div>;

  return (
    <section className="lp-page lp-assets-page">
      <WizardSteps current="assets" />
      <div className="lp-page-head">
        <div>
          <span className="lp-section-kicker">ASSETS</span>
          <h1>素材管理</h1>
          <p>ページ設計に進む前に、写真・ロゴ・料金表などをまとめます。チャットで追加した素材もここに表示されます。</p>
        </div>
        <Link className="btn btn-primary" to="/page-plan">
          ページ設計へ進む
        </Link>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="lp-assets-hero">
        <div>
          <h2>まずは、ページ作成に使う素材を揃えます。</h2>
          <p>代表写真はアップロードしておけば、エンジニアがサイト制作時にプロフィール画像生成工程へ回します。</p>
        </div>
        <div className="lp-assets-score">
          <strong>{confirmedCount}/{REQUIRED_ASSETS.length}</strong>
          <span>確認済み</span>
        </div>
      </div>

      <div className="lp-assets-workbench">
        <div className="lp-card lp-asset-board">
          <div className="lp-section-head">
            <div>
              <h2>素材を追加する</h2>
              <p className="muted">各カードから直接アップロードできます。ファイル追加は1件{CREDIT_RATES.assetUpload}cr、文章保存は1回{CREDIT_RATES.assetText}crを使用します。</p>
            </div>
          </div>
          <div className="lp-asset-type-grid">
            {REQUIRED_ASSETS.map((asset) => {
              const items = byKind.get(asset.kind) ?? [];
              const textKey = asset.kind === "testimonial" ? "testimonial_text" : asset.kind === "fee_table" ? "fee_table_text" : null;
              const textValue = textKey ? assetTexts[textKey] : "";
              const showTextInput = textKey ? textOpen[textKey] || Boolean(textValue.trim()) : false;
              const status = statusFor(items.length + (textValue.trim() ? 1 : 0), asset.required);
              return (
                <div key={asset.kind} className="lp-asset-type-card">
                  <div className="lp-asset-type-head">
                    <span className="lp-square-icon">{asset.name.slice(0, 1)}</span>
                    <div>
                      <strong>{asset.name}</strong>
                      <small>{asset.accept}</small>
                    </div>
                    <em className={`lp-status-pill ${status.tone}`}>{status.label}</em>
                  </div>
                  <p>{asset.note}</p>
                  {textKey ? (
                    <div className="lp-asset-add-menu-wrap">
                      <button
                        type="button"
                        className="lp-upload-zone compact as-button"
                        onClick={() => setChoiceOpenKind((current) => current === asset.kind ? null : asset.kind)}
                      >
                        <span>＋</span>
                        <strong>{busyKind === asset.kind ? "アップロード中..." : `素材を追加（${CREDIT_RATES.assetUpload}cr〜）`}</strong>
                      </button>
                      {choiceOpenKind === asset.kind && (
                        <div className="lp-asset-add-menu" role="menu" aria-label={`${asset.name}の追加方法`}>
                          <label role="menuitem">
                            <input type="file" multiple hidden onChange={(e) => {
                              setChoiceOpenKind(null);
                              void onFiles(asset.kind, e.target.files);
                            }} />
                            ファイルを選ぶ（1件{CREDIT_RATES.assetUpload}cr）
                          </label>
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => {
                              setTextOpen((current) => ({ ...current, [textKey]: true }));
                              setChoiceOpenKind(null);
                            }}
                          >
                            文章で入力する（保存時{CREDIT_RATES.assetText}cr）
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <label className="lp-upload-zone compact">
                      <input type="file" multiple hidden onChange={(e) => void onFiles(asset.kind, e.target.files)} />
                      <span>＋</span>
                      <strong>{busyKind === asset.kind ? "アップロード中..." : `ファイルを追加（${CREDIT_RATES.assetUpload}cr）`}</strong>
                    </label>
                  )}
                  {textKey && showTextInput && (
                    <div className="lp-text-asset-box">
                      <label>
                        {asset.kind === "testimonial" ? "文章で入力する" : "料金表を文章で入力する"}
                        <textarea
                          rows={4}
                          value={textValue}
                          onChange={(e) => setAssetTexts((current) => ({ ...current, [textKey]: e.target.value }))}
                          placeholder={asset.kind === "testimonial"
                            ? "例）福岡市の建設業者様：許可要件の確認から申請まで丁寧に対応しました。"
                            : "例）建設業許可 新規申請 150,000円から / 更新 70,000円から / 個別見積もり"}
                        />
                      </label>
                      <button className="btn btn-ghost btn-sm" type="button" onClick={() => void saveTextAsset(textKey)} disabled={textSaving === textKey}>
                        {textSaving === textKey ? "保存中..." : `文章を保存（${CREDIT_RATES.assetText}cr）`}
                      </button>
                    </div>
                  )}
                  {items.length > 0 && (
                    <div className="lp-file-list compact">
                      {items.slice(0, 3).map((item) => (
                        <a key={item.id} href={item.url} target="_blank" rel="noreferrer">
                          {isImage(item.content_type) ? <img src={item.url} alt={item.filename ?? ""} /> : <span className="lp-file-icon">FILE</span>}
                          <strong>{item.filename ?? "素材"}</strong>
                          <span>{fileSize(item.size)}</span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <aside className="lp-assets-side">
          <div className="lp-card">
            <h2>確認リスト</h2>
            <div className="lp-asset-list compact">
              {REQUIRED_ASSETS.map((asset) => {
                const count = byKind.get(asset.kind)?.length ?? 0;
                const status = statusFor(count + (hasTextAsset(asset.kind) ? 1 : 0), asset.required);
                return (
                  <div key={asset.kind}>
                    <span className="lp-square-icon">{asset.name.slice(0, 1)}</span>
                    <div>
                      <strong>{asset.name}</strong>
                      <small>{asset.note}</small>
                    </div>
                    <span className={`lp-status-pill ${status.tone}`}>{status.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </aside>
      </div>

      <div className="lp-card">
        <div className="lp-section-head">
          <div>
            <h2>その他の素材</h2>
            <p className="muted">面談資料・パンフレット・名刺など、AIに読ませたい資料を追加できます。</p>
          </div>
        </div>
        <div className="lp-extra-upload-row">
          {UPLOAD_KINDS.filter((kind) => !REQUIRED_ASSETS.some((asset) => asset.kind === kind.value)).map((kind) => {
            const items = byKind.get(kind.value) ?? [];
            return (
              <div key={kind.value} className="lp-extra-upload-card">
                <strong>{kind.label}</strong>
                <small>{kind.hint}</small>
                <label className="btn btn-ghost btn-sm">
                  <input type="file" multiple hidden onChange={(e) => void onFiles(kind.value, e.target.files)} />
                  {busyKind === kind.value ? "アップロード中..." : "追加する"}
                </label>
                <span>{items.length}件</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
